import { Navigate, useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';

import { useWrapped } from '@/hooks/useWrapped';
import { WrappedContainer } from '@/components/Wrapped/WrappedContainer';
import { AppRoutesEnum } from '@/lib/constants';
import config from '@/config';
import { MOCK_WRAPPED_DATA } from '@/api/mockWrappedData';

export default function WrappedPage() {
  const [searchParams] = useSearchParams();
  const isDebug = searchParams.get('debug') === 'true';
  const queryClient = useQueryClient();
  const { canAccess, accessReason, isAccessLoading, data, isLoading, error, markComplete } = useWrapped();

  // Use mock data if debug mode is enabled
  const displayData = isDebug ? MOCK_WRAPPED_DATA : data;
  
  // Override access check for debug mode
  const hasAccess = isDebug ? true : canAccess;

  // Check frontend feature flag first
  if (!config.ENABLE_WRAPPED && !isDebug) {
    return (
      <div className="fixed inset-0 bg-[#0a0a14] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-display text-white mb-4">Wrapped 2025</h1>
          <p className="text-white/70 mb-6">Wrapped is not currently available.</p>
          <a
            href={AppRoutesEnum.Home}
            className="inline-block px-6 py-3 bg-primary text-black font-bold rounded-lg hover:bg-primary/90 transition-colors"
          >
            Return Home
          </a>
        </div>
      </div>
    );
  }

  // Check if wrapped has been archived
  const endDate = new Date(config.WRAPPED_END_DATE);
  if (new Date() > endDate && !isDebug) {
    return (
      <div className="fixed inset-0 bg-[#0a0a14] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-display text-white mb-4">Wrapped 2025</h1>
          <p className="text-white/70 mb-6">Wrapped 2025 has been archived. Thanks for participating!</p>
          <a
            href={AppRoutesEnum.Home}
            className="inline-block px-6 py-3 bg-primary text-black font-bold rounded-lg hover:bg-primary/90 transition-colors"
          >
            Return Home
          </a>
        </div>
      </div>
    );
  }

  // Show loading state
  if ((isAccessLoading || isLoading) && !isDebug) {
    return (
      <div className="fixed inset-0 bg-[#0a0a14] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/70 font-display">Loading your Wrapped...</p>
        </div>
      </div>
    );
  }

  // Check access
  if (!hasAccess) {
    return (
      <div className="fixed inset-0 bg-[#0a0a14] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-display text-white mb-4">Wrapped 2025</h1>
          <p className="text-white/70 mb-6">{accessReason || 'Unable to access Wrapped at this time.'}</p>
          <a
            href={AppRoutesEnum.Home}
            className="inline-block px-6 py-3 bg-primary text-black font-bold rounded-lg hover:bg-primary/90 transition-colors"
          >
            Return Home
          </a>
        </div>
      </div>
    );
  }

  // Show error state
  if ((error || !displayData) && !isDebug) {
    return (
      <div className="fixed inset-0 bg-[#0a0a14] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-display text-white mb-4">Oops!</h1>
          <p className="text-white/70 mb-6">
            {error?.message || 'Something went wrong loading your Wrapped.'}
          </p>
          <a
            href={AppRoutesEnum.Home}
            className="inline-block px-6 py-3 bg-primary text-black font-bold rounded-lg hover:bg-primary/90 transition-colors"
          >
            Return Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Junk Fun Wrapped is only the start of something new. If you've discovered this message, then congratulations, you have just won 10,000 points. You'd better share this to your friends as it would be an important element to …🤫 */}
      <WrappedContainer
        data={displayData!}
        onComplete={() => markComplete()}
        onShare={() => {
          // Invalidate caches after share verification (API call handled by modal)
          queryClient.invalidateQueries({ queryKey: ['wrapped', 'data'] });
          queryClient.invalidateQueries({ queryKey: ['points'] });
        }}
      />
    </>
  );
}
